import { Component } from '@theme/component';
import { fetchConfig, isClickedOutside, onAnimationEnd } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Cart Quick Add: a variant chooser scoped to the cart (drawer or page) for the
 * upsell/recommendation cards, so a multi-variant product is never added blind
 * with its default variant.
 *
 * One shared dialog shell per recommendations strip (rendered by
 * `snippets/cart-recommendations.liquid`); each multi-variant card carries a
 * Liquid-rendered <template data-cart-quick-add-template> (image, title,
 * price, option groups, quantity stepper, add-to-cart) plus a variants JSON.
 * The card's "+ Add" trigger targets this component by id
 * (`on:click="#CartQuickAdd-<section>/openFromTrigger"`) and the template is
 * cloned into the dialog body on open — the carousel itself is never touched,
 * so opening/closing can't reset or re-render it.
 *
 * Presentation is CSS-only (see cart-recommendations.liquid): a lightweight
 * sub-modal anchored over the cart drawer panel (or centered on the cart page)
 * on desktop, a bottom sheet on mobile. The native <dialog> top layer stacks
 * it above the (still open) cart drawer dialog.
 *
 * This intentionally does NOT extend DialogComponent: nested inside the open
 * cart drawer the body is already scroll-locked by the drawer's
 * DialogComponent, and locking again would capture window.scrollY as 0 and
 * clobber the drawer's saved offset on restore. We only lock/unlock the body
 * when we're the outermost dialog (the cart page).
 *
 * The add POSTs to cart/add with `sections` (same fast path as
 * cart-line-upsell.js) and dispatches CartAddEvent only AFTER the dialog has
 * fully closed — dispatching earlier would let cart-items-component morph the
 * section (this dialog included) while it's still open in the top layer.
 *
 * @typedef {object} CartQuickAddRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} body - Container the card template is cloned into.
 * @property {HTMLElement} [liveRegion] - Visually-hidden status region.
 *
 * @typedef {object} QuickAddVariant
 * @property {number} id
 * @property {boolean} available
 * @property {string[]} options
 * @property {string} price - Pre-formatted money string.
 * @property {string | null} compare - Pre-formatted compare-at, null when not on sale.
 * @property {number} qmin
 * @property {number | null} qmax
 * @property {number} qstep
 * @property {string | null} image
 *
 * @extends Component<CartQuickAddRefs>
 */
class CartQuickAddComponent extends Component {
  requiredRefs = ['dialog', 'body'];

  /** @type {QuickAddVariant[]} */
  #variants = [];

  /** @type {(string | null)[]} */
  #selected = [];

  #optionCount = 0;

  /** @type {string | null} */
  #productId = null;

  #adding = false;

  #lockedBody = false;

  #previousScrollY = 0;

  /**
   * Opens the quick add for the card whose "+ Add" trigger was clicked.
   * The trigger and its product template live in the same `.cart-upsell` card.
   *
   * @param {Event} event
   */
  openFromTrigger(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-cart-quick-add-trigger]');
    const template = trigger?.closest('.cart-upsell')?.querySelector('template[data-cart-quick-add-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    this.#populate(template);
    this.#show();
  }

  /** @param {HTMLTemplateElement} template */
  #populate(template) {
    const { body, dialog } = this.refs;

    body.replaceChildren(template.content.cloneNode(true));

    this.#productId = template.dataset.productId ?? null;
    const title = template.dataset.productTitle;
    if (title) dialog.setAttribute('aria-label', title);

    const variantsScript = body.querySelector('script[data-cqa-variants]');
    try {
      this.#variants = variantsScript ? JSON.parse(variantsScript.textContent ?? '[]') : [];
    } catch {
      this.#variants = [];
    }

    this.#optionCount = body.querySelectorAll('[data-cqa-option-index]').length;
    this.#selected = new Array(this.#optionCount).fill(null);
    this.#adding = false;

    // Single-value options render as a fixed label/value row (no control);
    // their value is pre-selected here — there's nothing to choose.
    for (const group of body.querySelectorAll('[data-cqa-option-index]')) {
      if (!(group instanceof HTMLElement)) continue;
      const fixed = group.dataset.cqaFixedValue;
      if (fixed === undefined) continue;
      const index = Number(group.dataset.cqaOptionIndex);
      if (!Number.isNaN(index)) this.#selected[index] = fixed;
    }

    this.#refresh();
  }

  #show() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Only the outermost dialog manages the body scroll lock: on the cart
    // page we're it; inside the drawer the drawer's DialogComponent already
    // fixed the body (re-locking here would read scrollY as 0 and overwrite
    // the drawer's saved position).
    if (document.body.style.position !== 'fixed') {
      this.#lockedBody = true;
      this.#previousScrollY = window.scrollY;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;
    }

    dialog.showModal();

    // A native cancel (Escape while focus sits OUTSIDE the dialog subtree —
    // e.g. a failed add disables the focused submit mid-flight, which blurs
    // focus to <body>, so the keydown never bubbles through this component)
    // would close the dialog WITHOUT our cleanup: the body scroll lock (cart
    // page) would never release and the dialog body would keep stale content.
    // Route it through closeDialog instead. Attached per open; identical
    // handler refs are deduped by the platform.
    dialog.addEventListener('cancel', this.#handleCancel);

    // Same iOS quirk as dialog.js: showModal()'s auto-focus paints a
    // :focus-visible ring on the first control even for a tap. Focus the
    // dialog surface instead; keyboard users get the ring on Tab.
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });

    // Arm outside-click/escape on the next tick so the opening tap can't be
    // caught as an outside click (same race dialog.js guards against).
    setTimeout(() => {
      if (!dialog.open) return;
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    }, 0);
  }

  /**
   * Closes the dialog, playing the standard closing animation. Public so the
   * shell's close button can target it declaratively.
   */
  closeDialog = async () => {
    const { dialog, body } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Restart the animation state (mirrors DialogComponent.closeDialog).
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    try {
      if (this.#lockedBody) {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      }
    } finally {
      // The close must always complete (see dialog.js for the failure mode).
      this.#lockedBody = false;
      dialog.close();
      dialog.classList.remove('dialog-closing');
      // Empty the body so the server-rendered (empty) markup matches on the
      // next section morph and stale radio state can't leak into a reopen.
      body.replaceChildren();
      this.#restoreFocusToHost();
    }
  };

  /**
   * If closing dropped focus outside the (still open) host cart drawer —
   * native restoration fails when the opening trigger is gone, e.g. the
   * card was removed by the cart morph — pull focus back onto the drawer
   * dialog. Otherwise the next Escape targets document.body, bypassing the
   * drawer's own keydown handler, and the browser's native cancel closes the
   * drawer WITHOUT running DialogComponent's body-unlock cleanup, leaving the
   * page permanently scroll-locked.
   */
  #restoreFocusToHost() {
    const host = this.closest('dialog');
    if (!host || !host.open || this.refs.dialog === host) return;

    // Focus still inside our just-closed dialog is stale too: the browser's
    // focus fixup moves it to document.body AFTER the current task, so
    // reading activeElement here would wrongly report it as "inside the
    // drawer" and skip the restore.
    const active = document.activeElement;
    const stale = !active || active === document.body || this.refs.dialog.contains(active);
    if (!stale && host.contains(active)) return;

    if (!host.hasAttribute('tabindex')) host.setAttribute('tabindex', '-1');
    host.focus({ preventScroll: true });
  }

  /**
   * Outside clicks close only this dialog. stopPropagation keeps the same
   * click from reaching the cart drawer's own outside-click handler (we're a
   * DOM descendant of cart-drawer-component), which would otherwise also
   * close the drawer when the backdrop click lands off the drawer panel.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    event.stopPropagation();

    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) this.closeDialog();
  };

  /**
   * See the note at the addEventListener site in #show: a body-targeted
   * Escape fires the dialog's native cancel instead of bubbling through this
   * component, and the native close would skip closeDialog's cleanup.
   *
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    if (this.refs.dialog.classList.contains('dialog-closing')) return;
    this.closeDialog();
  };

  /**
   * Escape closes only this dialog, not the cart drawer beneath it.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  };

  /** @param {Event} event */
  handleOptionChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;

    const fieldset = input.closest('[data-cqa-option-index]');
    if (!(fieldset instanceof HTMLElement)) return;

    const index = Number(fieldset.dataset.cqaOptionIndex);
    if (Number.isNaN(index)) return;

    // A select's disabled placeholder has value '' — treat as "not chosen".
    this.#selected[index] = input.value || null;
    this.#refresh();
  }

  increaseQuantity() {
    this.#stepQuantity(1);
  }

  decreaseQuantity() {
    this.#stepQuantity(-1);
  }

  /** @param {number} direction */
  #stepQuantity(direction) {
    const input = this.#quantityInput;
    if (!input) return;

    const step = Number(input.step) || 1;
    input.value = String(this.#clamp((Number(input.value) || 0) + direction * step));
  }

  clampQuantity() {
    const input = this.#quantityInput;
    if (!input) return;

    input.value = String(this.#clamp(Number(input.value)));
  }

  /**
   * Clamps a quantity to the resolved variant's min/max and snaps it to the
   * step increment.
   *
   * @param {number} value
   * @returns {number}
   */
  #clamp(value) {
    const input = this.#quantityInput;
    const min = Number(input?.min) || 1;
    const max = input?.max ? Number(input.max) : Infinity;
    const step = Number(input?.step) || 1;

    if (Number.isNaN(value)) return min;

    let clamped = Math.max(min, Math.min(max, value));
    clamped = min + Math.round((clamped - min) / step) * step;
    return Math.max(min, Math.min(max, clamped));
  }

  /** @param {Event} event */
  addToCart(event) {
    event.preventDefault();

    const variant = this.#resolvedVariant;
    const submit = this.#submitButton;
    if (!variant || !variant.available || !submit || this.#adding) return;

    this.#adding = true;
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    this.#showError(null);

    const productId = this.#productId;
    const quantity = this.#quantityInput ? this.#clamp(Number(this.#quantityInput.value)) : 1;

    const formData = new FormData();
    formData.set('id', String(variant.id));
    formData.set('quantity', String(quantity));

    // Ask cart/add to re-render every cart-items section on the page (cart
    // page or drawer) so the new line shows up without a full reload.
    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.#showError(response.description || this.dataset.textError || '');
          this.#adding = false;
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
          return;
        }

        if (this.refs.liveRegion) this.refs.liveRegion.textContent = this.dataset.textAdded ?? '';

        // Close (and clear) BEFORE announcing the cart change: the morph the
        // event triggers re-renders this whole section, and morphing an open
        // top-layer dialog would strip its [open] state mid-animation.
        await this.closeDialog();

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'cart-quick-add',
            productId: productId ?? undefined,
            variantId: String(variant.id),
            sections: response.sections,
          })
        );

        // The morph the event just triggered may have removed the card whose
        // trigger held focus; keep focus inside the drawer (see the note on
        // #restoreFocusToHost).
        this.#restoreFocusToHost();
      })
      .catch((error) => {
        console.error(error);
        this.#showError(this.dataset.textError || '');
        this.#adding = false;
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      });
  }

  /** @param {string | null} message */
  #showError(message) {
    const error = this.refs.body.querySelector('[data-cqa-error]');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message ?? '';
    error.hidden = !message;
  }

  get #quantityInput() {
    const input = this.refs.body.querySelector('[data-cqa-quantity]');
    return input instanceof HTMLInputElement ? input : null;
  }

  get #submitButton() {
    const button = this.refs.body.querySelector('[data-cqa-submit]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  get #allSelected() {
    return this.#optionCount > 0 && this.#selected.every((value) => value !== null);
  }

  /**
   * The variant matching the full current selection, or null when the
   * selection is incomplete or the combination doesn't exist.
   *
   * @returns {QuickAddVariant | null}
   */
  get #resolvedVariant() {
    if (!this.#allSelected) return null;
    return this.#variants.find((variant) => variant.options.every((option, i) => option === this.#selected[i])) ?? null;
  }

  /**
   * Variants matching the current selection in every group EXCEPT
   * `excludeIndex`, further narrowed to `value` at that index.
   *
   * @param {number} excludeIndex
   * @param {string} value
   * @returns {QuickAddVariant[]}
   */
  #candidatesFor(excludeIndex, value) {
    return this.#variants.filter((variant) =>
      variant.options.every((option, i) => {
        if (i === excludeIndex) return option === value;
        const selected = this.#selected[i];
        return selected === null || option === selected;
      })
    );
  }

  /**
   * Re-derives the whole UI from the current selection: per-value
   * existence/availability, price, quantity rules, and the submit state.
   */
  #refresh() {
    const { body } = this.refs;

    for (const fieldset of body.querySelectorAll('[data-cqa-option-index]')) {
      if (!(fieldset instanceof HTMLElement)) continue;

      // Fixed single-value rows render no control; nothing to update.
      if (fieldset.dataset.cqaFixedValue !== undefined) continue;

      const index = Number(fieldset.dataset.cqaOptionIndex);

      // Dropdown-style option (e.g. any "Size" option — see the shared
      // variant-option-style snippet): values with no matching variant are
      // disabled, sold-out ones stay selectable with the PDP's
      // " - Unavailable" suffix (data-cqa-label holds the clean label).
      const select = fieldset.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        for (const option of select.options) {
          if (!option.value) continue; // the "Choose" placeholder

          const candidates = this.#candidatesFor(index, option.value);
          const exists = candidates.length > 0;
          const available = candidates.some((variant) => variant.available);
          const label = option.dataset.cqaLabel ?? option.value;

          // Never disable the currently-chosen value or the shopper couldn't
          // move off it; the resolved-variant check still gates the add.
          option.disabled = !exists && select.value !== option.value;
          option.textContent = exists && !available ? `${label} - ${this.dataset.textUnavailable ?? ''}` : label;
        }
        continue;
      }

      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        if (!(input instanceof HTMLInputElement)) continue;

        const candidates = this.#candidatesFor(index, input.value);
        const exists = candidates.length > 0;
        const available = candidates.some((variant) => variant.available);

        // A combination that doesn't exist at all is not selectable; a
        // sold-out one stays selectable (muted via the picker's own
        // data-option-available styling) so the shopper can see the state.
        input.disabled = !exists;
        input.dataset.optionAvailable = String(available);
        input.closest('label')?.classList.toggle('cart-quick-add__value--nonexistent', !exists);
      }
    }

    const variant = this.#resolvedVariant;

    this.#refreshPrice(variant);
    this.#refreshQuantity(variant);
    this.#refreshSubmit(variant);
  }

  /** @param {QuickAddVariant | null} variant */
  #refreshPrice(variant) {
    const { body } = this.refs;
    const priceEl = body.querySelector('[data-cqa-price]');
    const current = body.querySelector('[data-cqa-current]');
    const compare = body.querySelector('[data-cqa-compare]');
    if (!variant || !(current instanceof HTMLElement)) return;

    current.textContent = variant.price;
    if (compare instanceof HTMLElement) {
      compare.textContent = variant.compare ?? '';
      compare.hidden = !variant.compare;
    }
    if (priceEl instanceof HTMLElement) {
      priceEl.classList.toggle('cart-quick-add__price--on-sale', Boolean(variant.compare));
    }

    const image = body.querySelector('[data-cqa-image]');
    if (variant.image && image instanceof HTMLImageElement && image.src !== variant.image) {
      image.src = variant.image;
      image.removeAttribute('srcset');
    }
  }

  /** @param {QuickAddVariant | null} variant */
  #refreshQuantity(variant) {
    const input = this.#quantityInput;
    if (!input) return;

    input.min = String(variant?.qmin ?? 1);
    input.step = String(variant?.qstep ?? 1);
    if (variant?.qmax) {
      input.max = String(variant.qmax);
    } else {
      input.removeAttribute('max');
    }
    input.value = String(this.#clamp(Number(input.value)));
  }

  /** @param {QuickAddVariant | null} variant */
  #refreshSubmit(variant) {
    const submit = this.#submitButton;
    const label = this.refs.body.querySelector('[data-cqa-submit-label]');
    if (!submit) return;

    let text = this.dataset.textAddToCart ?? '';
    let disabled = true;

    if (!this.#allSelected) {
      // Options still missing: keep "Add to cart", just not actionable yet.
    } else if (!variant) {
      text = this.dataset.textUnavailable ?? text;
    } else if (!variant.available) {
      text = this.dataset.textSoldOut ?? text;
    } else {
      disabled = this.#adding;
    }

    submit.disabled = disabled;
    if (label instanceof HTMLElement) label.textContent = text;
  }
}

if (!customElements.get('cart-quick-add-component')) {
  customElements.define('cart-quick-add-component', CartQuickAddComponent);
}
